/**
 * Types de ligne déduits de la structure du tableau (cf. getTableRowStructure),
 * pour les lignes que l'API ne type pas : le titre d'un poste ou d'une tenue
 * (`title`), dont le libellé change d'une affaire à l'autre, et la TVA
 * (`vat`), dont le libellé porte le taux. Une règle de fusion ou de style par
 * type vaut alors pour toutes ces lignes, comme pour les zones.
 *
 * Déduits de la ligne, ils suivent le rafraîchissement sans relire la table.
 *
 * Module partagé document ↔ site : pur, sans React ni store.
 */

import { hasStaleRowKinds } from './tableColumns';

// Types que le front déduit lui-même : une règle qui les vise n'attend pas
// l'API (pas de rafraîchissement à demander, cf. hasStaleRowKinds).
export const FRONT_ROW_KINDS = ['empty', 'title', 'vat'];

const defaultColumnLabel = (col) => col.label ?? col.key;

/**
 * `data` avec le type déduit de chaque ligne qui en a un ; les autres lignes
 * restent les mêmes objets. Sans structure, `data` tel quel.
 * `options` : `isEmptyValue`, `getText` (texte nu) et `columnLabel` (libellé
 * d'origine d'une colonne).
 */
export const withStructureRowKinds = (data, columns, structure, options = {}) => {
  if (!structure || !Array.isArray(data) || !Array.isArray(columns) || columns.length === 0) return data;
  const {
    isEmptyValue = (value) => value == null || String(value).trim() === '',
    getText = (value) => (value == null ? '' : String(value)).trim(),
    columnLabel = defaultColumnLabel,
  } = options;
  const ignored = new Set(columns.filter((col) => structure.titleIgnore?.includes(columnLabel(col))).map((col) => col.key));
  const fixed = new Set(structure.fixedLabels);

  const kindOf = (row) => {
    if (row.rowKind || row.manual) return null;
    // Cases remplies hors photo : le libellé est la première.
    const filled = columns.filter((col) => !ignored.has(col.key) && !isEmptyValue(row[col.key]));
    if (filled.length === 0) return null;
    const label = getText(row[filled[0].key]);
    if (!label) return null;
    const pattern = structure.kindPatterns?.find(({ match }) => match.test(label));
    if (pattern) return pattern.rowKind;
    if (structure.title && filled.length === 1 && !fixed.has(label)) return 'title';
    return null;
  };

  return data.map((row) => {
    const rowKind = kindOf(row);
    return rowKind ? { ...row, rowKind } : row;
  });
};

const needsApiRowKind = (rules) =>
  (rules ?? []).some((rule) => rule.kind && !FRONT_ROW_KINDS.includes(rule.kind));

/**
 * Une fusion ou un style par type de ligne (zone) ne s'applique qu'aux lignes
 * dont l'API a donné le type : des lignes lues avant ne le montrent qu'après
 * rafraîchissement. Les types que le front déduit (vide, titre, TVA) n'en ont
 * pas besoin.
 */
export const awaitsRowKindsRefresh = (tableConfig, rows) =>
  (needsApiRowKind(tableConfig?.merge?.overrides) || needsApiRowKind(tableConfig?.rowStyles)) &&
  hasStaleRowKinds(rows);
